/**
 * "How this module works" — a single collapsible summary of every capability
 * in the appraisal system. Visible to all staff so a technician can see
 * exactly how they are measured, scored and rewarded.
 */
import { useLang } from '../../../i18n/LangContext';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, ChevronDown, CalendarRange, Activity, ClipboardCheck, Calculator,
  Scale, LifeBuoy, IndianRupee, Grid3x3, GraduationCap, BadgeCheck, FileDown,
  Bell, ExternalLink,
} from 'lucide-react';
import { BAND_STYLE } from './shared';

const STORE_KEY = 'pms_guide_open';

const BANDS_FALLBACK = [
  { band: 'A', min: 90, label: 'Outstanding', action: 'Top increment + bonus' },
  { band: 'B', min: 75, label: 'Exceeds expectations', action: 'Above-average increment' },
  { band: 'C', min: 60, label: 'Meets expectations', action: 'Standard increment' },
  { band: 'D', min: 45, label: 'Needs improvement', action: 'Coaching, no increment' },
  { band: 'E', min: 0, label: 'Below expectations', action: 'Formal PIP (90 days)' },
];

const Section = ({ icon: Icon, title, to, hrOnly, points, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3.5" data-testid={testid}>
    <div className="flex items-start gap-2">
      <span className="mt-0.5 shrink-0 rounded-md bg-teal-50 p-1.5 text-teal-600">
        <Icon size={14} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
          {hrOnly && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              HR / manager
            </span>
          )}
        </div>
        <ul className="mt-1.5 space-y-1">
          {points.map((p) => (
            <li key={p} className="flex gap-1.5 text-xs leading-relaxed text-slate-600">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-300" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        {to && (          <Link
            to={to}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900"
            data-testid={`${testid}-link`}
          >
            Open screen <ExternalLink size={11} />
          </Link>
        )}
      </div>
    </div>
  </div>
);

const ModuleGuide = ({ meta, defaultOpen = false }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(() => {
    const stored = localStorage.getItem(STORE_KEY);
    return stored === null ? defaultOpen : stored === '1';
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(STORE_KEY, next ? '1' : '0');
  };

  const bands = meta?.bands?.length ? meta.bands : BANDS_FALLBACK;
  const isHr = !!meta?.capabilities?.is_hr;
  const w = meta?.weights?.with_skills || { goals: 0.5, competencies: 0.2, skills: 0.3 };
  // Without a skill sheet the backend renormalises the same two weights to 100%.
  const wSum = Number(w.goals) + Number(w.competencies);
  const wNoSkill = { goals: w.goals / wSum, competencies: w.competencies / wSum };
  const cap = meta?.weights?.achievement_cap ?? 120;
  const pc = (n) => `${Math.round(Number(n) * 100)}%`;

  const sections = [
    {
      testid: 'guide-cycles', icon: CalendarRange, title: 'Review cycles (twice a year)', hrOnly: true,
      to: '/admin/performance',
      points: [
        'H1 covers Apr–Sep, H2 covers Oct–Mar, so every member is formally reviewed twice a year.',
        '"Enrol staff" creates a review sheet for every active member using the default goal template.',
        'The cycle moves through Goal Setting → Self Review → Manager Review → Calibration → Published.',
      ],
    },
    {
      testid: 'guide-metrics', icon: Activity, title: 'Goals with auto-tracked numbers',
      points: [
        'Jobs completed, on-time delivery, inspections, attendance, OT hours and teammates lifted are pulled live from the system — nobody types them in.',
        'Each goal carries a weight and a target; achievement is capped at ' + cap + '% so one runaway number cannot hide a weak area.',
        'Manual goals (e.g. housekeeping, customer compliments) can be added alongside the automatic ones.',
      ],
    },
    {
      testid: 'guide-reviews', icon: ClipboardCheck, title: 'Self review → manager review → publish',
      to: '/admin/my-performance',
      points: [
        'Staff rate themselves 1–5 on each behaviour and can add a note against every goal.',
        'The reporting manager then scores the same behaviours, writes feedback and picks a recommendation (increment, increment + promotion, hold, or PIP).',
        'HR approves and publishes. Once published the sheet locks and the staff member sees the full result.',
      ],
    },
    {
      testid: 'guide-score', icon: Calculator, title: 'How the final score is built',
      points: [
        `With a skill sheet on file: goals ${pc(w.goals)} + behaviour ${pc(w.competencies)} + skill index ${pc(w.skills)}.`,
        `Without a skill sheet: goals ${pc(wNoSkill.goals)} + behaviour ${pc(wNoSkill.competencies)} — nobody is penalised for a missing assessment.`,
        'The score maps to a band from A to E, and the band drives the increment recommendation.',
      ],
    },
    {
      testid: 'guide-pdf', icon: FileDown, title: 'Appraisal letter (PDF)',
      points: [
        'Every published review generates a printable appraisal letter with scores, band, goal table and manager feedback.',
        'Staff can download their own letter; HR can download anyone\'s.',
      ],
    },
    {
      testid: 'guide-calibration', icon: Scale, title: 'Calibration board', hrOnly: true,
      to: '/admin/performance/calibration',
      points: [
        'Compares the actual band spread against a suggested spread so scoring stays fair across teams.',
        'Bands can be nudged before publishing, with a reason recorded against the change.',
      ],
    },
    {
      testid: 'guide-pips', icon: LifeBuoy, title: 'Improvement plans (30/60/90 days)', hrOnly: true,
      to: '/admin/performance/plans',
      points: [
        'A band E review auto-drafts a 90-day plan; plans can also be raised for anyone (e.g. a new joiner on probation).',
        'Checkpoints at day 30, 60 and 90 record progress, and the plan closes as passed or failed.',
      ],
    },
    {
      testid: 'guide-revisions', icon: IndianRupee, title: 'Salary revisions into payroll', hrOnly: true,
      to: '/admin/payroll',
      points: [
        'An approved increment % on a published review becomes a salary revision (old → new base salary, effective date).',
        'A nightly job applies revisions once the effective date arrives, so the next payroll run picks up the new salary automatically.',
        'HR can edit or cancel any revision before it is applied.',
      ],
    },
    {
      testid: 'guide-skills', icon: Grid3x3, title: 'Skill matrix & quarterly assessments', hrOnly: true,
      to: '/admin/skills',
      points: [
        'A grid of every skill against every member, colour-coded by level, so gaps are visible at a glance.',
        'Assessments are recorded each quarter and produce a skill index that feeds the appraisal score.',
        'Stale assessments trigger an automatic reminder so the matrix never goes out of date.',
      ],
    },
    {
      testid: 'guide-training', icon: GraduationCap, title: 'Training plans & mentorship', hrOnly: true,
      to: '/admin/skills/training',
      points: [
        'Every skill marked "not OK" becomes a training task with a trainer and a target date.',
        'The trainer earns a "teammates lifted" credit when the learner\'s level improves — and that credit counts in their own appraisal.',
      ],
    },
    {
      testid: 'guide-badges', icon: BadgeCheck, title: 'Expert badges & skill certificates',
      points: [
        'Members who reach expert level on a skill group carry a badge next to their name across the app.',
        'A printable skill certificate can be issued as recognition.',
        'When an appointment needs a skill the assigned member does not hold, the booking form shows a warning before saving.',
      ],
    },
    {
      testid: 'guide-reminders', icon: Bell, title: 'Reminders that keep the cycle moving',
      points: [
        'Weekly nudges go to staff with a pending self review and to managers with pending reportees, plus a summary to the ops inbox.',
        'HR can also fire the reminders on demand from the "Send reminders" button.',
      ],
    },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="module-guide">
      <button
        onClick={toggle}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-50"
        data-testid="module-guide-toggle"
        aria-expanded={open}
      >
        <BookOpen size={15} className="shrink-0 text-teal-600" />
        <span className="text-sm font-semibold text-slate-800">{t('How this module works')}</span>
        <span className="hidden text-xs text-slate-500 sm:inline">
            — {t('every feature of the appraisal system, in one place')}
        </span>
        <ChevronDown
          size={16}
          className={`ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t border-slate-200 bg-slate-50/60 p-4" data-testid="module-guide-body">
          <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3.5" data-testid="guide-bands">
            <h4 className="text-sm font-semibold text-slate-900">Bands and what they mean</h4>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {bands.map((b) => (
                <div key={b.band} className={`rounded-lg border px-2.5 py-2 ${BAND_STYLE[b.band]}`}>
                  <div className="text-xs font-semibold">{b.band} · {b.min}+ </div>
                  <div className="text-[11px] font-medium">{b.label}</div>
                  <div className="mt-0.5 text-[11px] opacity-80">{b.action}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            {sections.map((sec) => (
              <Section key={sec.testid} {...sec} to={sec.hrOnly && !isHr ? '' : sec.to} />
            ))}
          </div>

          <p className="mt-4 text-xs text-slate-500">
            Screens marked <span className="font-medium text-slate-600">HR / manager</span> are only reachable
            with the matching permission — the summary is shown to everyone so the whole team knows how the
            appraisal works end to end.
          </p>
        </div>
      )}
    </div>
  );
};

export default ModuleGuide;
